import { lazy, Suspense, useEffect, useState } from "react";
import type { BoostSummary, LaunchSummary, ProtocolStats } from "@bubblefun/shared";
import { getBoosts, getLaunches, getStats } from "./lib/api";
import { usePathname } from "./lib/router";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import HomePage from "./pages/HomePage";
import { demoLaunches, demoStats } from "./data/demo";
import { EXTERNAL_MARKET_SEEDS } from "./data/externalMarkets";

const TokenPage = lazy(() => import("./pages/TokenPage"));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage"));
const DocsPage = lazy(() => import("./pages/DocsPage"));
const RisksPage = lazy(() => import("./pages/RisksPage"));
const QuickLaunchModal = lazy(() => import("./components/QuickLaunchModal"));

function RouteLoading() {
  return <main className="route-loading"><span>BF / 4663</span><i /><p>Assembling the market sheet…</p></main>;
}

export default function App() {
  const pathname = usePathname();
  const [launches, setLaunches] = useState<LaunchSummary[]>(demoLaunches);
  const [stats, setStats] = useState<ProtocolStats>(demoStats);
  const [boosts, setBoosts] = useState<BoostSummary[]>([]);
  const [externalMarkets, setExternalMarkets] = useState<LaunchSummary[]>([...EXTERNAL_MARKET_SEEDS]);
  const [demo, setDemo] = useState(true);
  const [boostDemo, setBoostDemo] = useState(true);
  const [quickLaunchOpen, setQuickLaunchOpen] = useState(false);
  useEffect(() => {
    let disposed = false;
    const refresh = async () => {
      const [marketResult, statsResult, boostResult] = await Promise.all([getLaunches(), getStats(), getBoosts()]);
      if (disposed) return;
      setLaunches(marketResult.launches);
      setStats(statsResult.stats);
      setBoosts(boostResult.boosts);
      setDemo(marketResult.demo || statsResult.demo);
      setBoostDemo(boostResult.demo);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    void refresh();
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, 8_000);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      disposed = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    let disposed = false;
    void import("./lib/publicRpcCandles").then(({ refreshExternalMarketSummaries }) => refreshExternalMarketSummaries()).then((markets) => {
      if (!disposed) setExternalMarkets(markets);
    }).catch(() => {
      // Keep the verified allowlist visible without inventing market values when the public RPC is rate-limited.
    });
    return () => { disposed = true; };
  }, []);

  let page;
  if (pathname === "/") page = <HomePage launches={launches} externalMarkets={externalMarkets} boosts={boosts} stats={stats} demo={demo} boostDemo={boostDemo} onLaunch={() => setQuickLaunchOpen(true)} />;
  else if (pathname.startsWith("/token/")) page = <TokenPage launches={[...launches, ...externalMarkets]} boosts={boosts} address={decodeURIComponent(pathname.slice(7))} />;
  else if (pathname === "/portfolio") page = <PortfolioPage launches={launches} />;
  else if (pathname === "/docs") page = <DocsPage />;
  else if (pathname === "/risks") page = <RisksPage />;
  else page = <main className="page-shell"><div className="not-found"><span>404 / PAGE</span><h1>This page is not in the edition.</h1><a href="/">Return home</a></div></main>;

  return <><Header pathname={pathname} onLaunch={() => setQuickLaunchOpen(true)} /><Suspense fallback={<RouteLoading />}>{page}</Suspense>{!pathname.startsWith("/token/") && <Footer onLaunch={() => setQuickLaunchOpen(true)} />}{quickLaunchOpen && <Suspense fallback={null}><QuickLaunchModal onClose={() => setQuickLaunchOpen(false)} /></Suspense>}</>;
}
